import type { StageT } from "@/components/common/Stepper";
import type { StepT } from "@/components/common/Stepper";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

// New event flow: Events -> Event Details -> (Injection/Injection Details | Surgery/Surgery Details | Education | Shockwave/Shockwave Details)

const createEventSteps = (event: string): StepT[] => {
	if (event === "injection") {
		return [
			{
				key: "injection",
				stage: "future",
				words: "Injection",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/injection/$injectionId",
					params: (prev) => ({ ...prev }),
				},
			},
			{
				key: "injection-details",
				stage: "future",
				words: "Injection Details",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/injection/$injectionId/details",
					params: (prev) => ({ ...prev }),
				},
			},
		];
	}
	if (event === "surgery") {
		return [
			{
				key: "surgery",
				stage: "future",
				words: "Surgery",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/surgery/$surgeryId",
					params: (prev) => ({ ...prev }),
				},
			},
			{
				key: "surgery-details",
				stage: "future",
				words: "Surgery Details",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/surgery/$surgeryId/details",
					params: (prev) => ({ ...prev }),
				},
			},
		];
	}
	if (event === "shockwave") {
		return [
			{
				key: "shockwave",
				stage: "future",
				words: "Shockwave",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId",
					params: (prev) => ({ ...prev }),
				},
			},
			{
				key: "shockwave-details",
				stage: "future",
				words: "Shockwave Details",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId/details",
					params: (prev) => ({ ...prev }),
				},
			},
		];
	}
	if (event === "education") {
		return [
			{
				key: "education",
				stage: "future",
				words: "Education",
				route: {
					to: "/new/diary/$diaryId/events/$eventId/education/$educationId",
					params: (prev) => ({ ...prev }),
				},
			},
		];
	}
	return [];
};

const createSteps = (event: string): StepT[] => [
	{
		key: "events",
		stage: "current",
		words: "Events",
		route: {
			to: "/new/diary/$diaryId/events/$eventId",
			params: (prev) => ({ ...prev }),
		},
	},
	{
		key: "event-details",
		stage: "future",
		words: "Event Details",
		route: {
			to: "/new/diary/$diaryId/events/$eventId/details",
			params: (prev) => ({ ...prev }),
		},
	},
	...createEventSteps(event),
];

interface State {
	steps: StepT[];
	currentStep: string;
	event: string;
}

interface Actions {
	setCurrentStep: (step: string) => void;
	markStepComplete: (step: string) => void;
	updateStepStage: (stepKey: string, stage: StageT) => void;
	nextStep: () => void;
	previousStep: () => void;
	setEvent: (event: string) => void;
	setEventAndStep: (event: string, stepKey: string) => void;
	getEvent: () => string;
}

const initialState: State = {
	steps: createSteps("injection"), // default to injection for initial
	currentStep: "events",
	event: "injection",
};

export const useEventNavigationStore = create<State & Actions>()(
	devtools((set, get) => ({
		...initialState,
		setCurrentStep: (step) => {
			const { steps } = get();
			const currentIndex = steps.findIndex((s) => s.key === step);
			set(() => ({
				currentStep: step,
				steps: steps.map((s, index) => ({
					...s,
					stage:
						index === currentIndex
							? "current"
							: index < currentIndex
							? "past"
							: "future",
				})),
			}));
		},
		markStepComplete: (step) => {
			const { steps, currentStep } = get();
			const stepIndex = steps.findIndex((s) => s.key === step);
			const currentIndex = steps.findIndex((s) => s.key === currentStep);
			set(() => ({
				steps: steps.map((s, index) => ({
					...s,
					stage:
						index === currentIndex
							? "current"
							: index < currentIndex || index === stepIndex
							? "past"
							: "future",
				})),
			}));
		},
		updateStepStage: (stepKey, stage) => {
			set((state) => ({
				steps: state.steps.map((s) =>
					s.key === stepKey ? { ...s, stage } : s,
				),
			}));
		},
		nextStep: () => {
			const { currentStep, steps } = get();
			const currentIndex = steps.findIndex((s) => s.key === currentStep);
			const nextStep = steps[currentIndex + 1];
			if (nextStep) {
				set(() => ({
					currentStep: nextStep.key,
					steps: steps.map((s, index) => ({
						...s,
						stage:
							index === currentIndex + 1
								? "current"
								: index <= currentIndex
								? "past"
								: "future",
					})),
				}));
			}
		},
		previousStep: () => {
			const { currentStep, steps } = get();
			const currentIndex = steps.findIndex((s) => s.key === currentStep);
			const prevStep = steps[currentIndex - 1];
			if (prevStep) {
				set(() => ({
					currentStep: prevStep.key,
					steps: steps.map((s, index) => ({
						...s,
						stage:
							index === currentIndex - 1
								? "current"
								: index < currentIndex - 1
								? "past"
								: "future",
					})),
				}));
			}
		},
		setEvent: (event) => {
			set(() => ({
				steps: createSteps(event),
				currentStep: "events",
				event,
			}));
		},
		setEventAndStep: (event, stepKey) => {
			const newSteps = createSteps(event);
			const currentIndex = newSteps.findIndex((s) => s.key === stepKey);
			set({
				currentStep: stepKey,
				steps: newSteps.map((s, index) => ({
					...s,
					stage:
						index < currentIndex
							? "past"
							: index === currentIndex
							? "current"
							: "future",
				})),
				event,
			});
		},
		getEvent: () => get().event,
	})),
);

export function useEventStepper(stepKey: string, event?: string) {
	const { steps, setCurrentStep, setEventAndStep } = useEventNavigationStore();
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	useEffect(() => {
		const getEventFromPath = () => {
			const eventTypes = ["injection", "surgery", "shockwave", "education"];
			const pathSegments = pathname.split("/");
			return pathSegments.find((segment) => eventTypes.includes(segment));
		};

		const eventType = event || getEventFromPath();

		if (eventType) {
			setEventAndStep(eventType, stepKey);
		} else {
			setCurrentStep(stepKey);
		}
	}, [event, pathname, stepKey, setEventAndStep, setCurrentStep]);
	return { steps };
}


